import { useState, useRef, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Send,
  Paperclip,
  Globe,
  FileText,
  Loader2,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { ConversationList } from '@/features/chat/components/ConversationList';
import { MessageBubble } from '@/features/chat/components/MessageBubble';

interface Source {
  type: 'document' | 'web';
  title: string;
  url?: string;
  document_id?: string;
}

interface Message {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  sources?: Source[];
  token_count?: number;
  created_at: string;
}

interface Conversation {
  id: string;
  title: string;
  created_at: string;
  updated_at: string;
}

type ModelOption = 'auto' | 'cloud' | 'local';

export function ChatPage() {
  const queryClient = useQueryClient();
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [input, setInput] = useState('');
  const [webSearch, setWebSearch] = useState(false);
  const [docSearch, setDocSearch] = useState(false);
  const [model, setModel] = useState<ModelOption>('auto');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Fetch conversations
  const { data: conversations, isLoading: convsLoading } = useQuery({
    queryKey: ['chat-conversations'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('conversation')
        .select('*')
        .order('updated_at', { ascending: false });
      if (error) throw error;
      return data as Conversation[];
    },
  });

  // Fetch messages for selected conversation
  const { data: messages, isLoading: msgsLoading } = useQuery({
    queryKey: ['chat-messages', selectedId],
    queryFn: async () => {
      if (!selectedId) return [];
      const { data, error } = await supabase
        .from('message')
        .select('*')
        .eq('conversation_id', selectedId)
        .order('created_at', { ascending: true });
      if (error) throw error;
      return data as unknown as Message[];
    },
    enabled: !!selectedId,
  });

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Send message
  const sendMessage = useMutation({
    mutationFn: async (content: string) => {
      const { data, error } = await supabase.functions.invoke('chat', {
        body: {
          conversation_id: selectedId,
          content,
          web_search: webSearch,
          doc_search: docSearch,
          model,
        },
      });
      if (error) throw error;
      return data as { conversation_id: string; message: Message };
    },
    onSuccess: (result) => {
      if (!selectedId) {
        setSelectedId(result.conversation_id);
      }
      queryClient.invalidateQueries({ queryKey: ['chat-messages', result.conversation_id] });
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
    },
  });

  // Create new conversation
  const handleCreate = () => {
    setSelectedId(null);
    setInput('');
  };

  // Delete conversation
  const deleteConversation = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('conversation')
        .delete()
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_, id) => {
      if (selectedId === id) setSelectedId(null);
      queryClient.invalidateQueries({ queryKey: ['chat-conversations'] });
    },
  });

  const handleSend = () => {
    const trimmed = input.trim();
    if (!trimmed || sendMessage.isPending) return;
    setInput('');
    sendMessage.mutate(trimmed);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="flex h-[calc(100vh-3.5rem)]">
      {/* Left sidebar - conversations */}
      <div className="w-72 shrink-0 border-r bg-card">
        {convsLoading ? (
          <div className="space-y-2 p-3">
            <Skeleton className="h-9 w-full" />
            {Array.from({ length: 6 }).map((_, i) => (
              <Skeleton key={i} className="h-14 w-full" />
            ))}
          </div>
        ) : (
          <ConversationList
            conversations={conversations ?? []}
            selectedId={selectedId}
            onSelect={setSelectedId}
            onCreate={handleCreate}
            onDelete={(id) => deleteConversation.mutate(id)}
          />
        )}
      </div>

      {/* Right panel - messages + input */}
      <div className="flex flex-1 flex-col">
        {/* Messages area */}
        <div className="flex-1 overflow-y-auto p-4">
          {selectedId && msgsLoading ? (
            <div className="space-y-4">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className={cn('flex', i % 2 === 0 ? 'justify-end' : 'justify-start')}>
                  <Skeleton className="h-16 w-[60%] rounded-2xl" />
                </div>
              ))}
            </div>
          ) : !selectedId && !messages?.length ? (
            <div className="flex h-full items-center justify-center">
              <div className="text-center">
                <h2 className="text-xl font-semibold">Start a conversation</h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Ask a question, search documents, or get AI assistance
                </p>
              </div>
            </div>
          ) : (
            <div className="space-y-1">
              {(messages ?? []).map((msg) => (
                <MessageBubble key={msg.id} message={msg} />
              ))}
              {sendMessage.isPending && (
                <div className="flex justify-start mb-4">
                  <div className="flex items-center gap-2 rounded-2xl rounded-bl-md border bg-card px-4 py-3">
                    <Loader2 className="h-4 w-4 animate-spin text-primary" />
                    <span className="text-sm text-muted-foreground">Thinking...</span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>
          )}
        </div>

        {/* Input area */}
        <div className="border-t bg-card p-4">
          <div className="mx-auto max-w-3xl space-y-2">
            {/* Toggles row */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setWebSearch(!webSearch)}
                className={cn(
                  'flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                  webSearch
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-accent',
                )}
              >
                <Globe className="h-3.5 w-3.5" />
                Web Search
              </button>
              <button
                onClick={() => setDocSearch(!docSearch)}
                className={cn(
                  'flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium transition-colors',
                  docSearch
                    ? 'border-primary bg-primary/10 text-primary'
                    : 'text-muted-foreground hover:bg-accent',
                )}
              >
                <FileText className="h-3.5 w-3.5" />
                Doc Search
              </button>
              <div className="ml-auto">
                <Select value={model} onValueChange={(v) => setModel(v as ModelOption)}>
                  <SelectTrigger className="h-7 w-28 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="auto">Auto</SelectItem>
                    <SelectItem value="cloud">Cloud</SelectItem>
                    <SelectItem value="local">Local</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Textarea + buttons */}
            <div className="flex items-end gap-2">
              <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                onChange={(e) => {
                  const file = e.target.files?.[0];
                  if (file) {
                    setInput((prev) => prev + (prev ? ' ' : '') + `[Attached: ${file.name}]`);
                  }
                  e.target.value = '';
                }}
              />
              <Button
                variant="ghost"
                size="icon"
                className="shrink-0"
                title="Attach file"
                onClick={() => fileInputRef.current?.click()}
              >
                <Paperclip className="h-4 w-4" />
              </Button>
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Type your message..."
                rows={1}
                className="min-h-[40px] max-h-32 resize-none"
              />
              <Button
                size="icon"
                className="shrink-0"
                onClick={handleSend}
                disabled={!input.trim() || sendMessage.isPending}
              >
                {sendMessage.isPending ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
